import { useEffect, useMemo, useState } from 'react'
import Layout from 'containers/Layout'
import moment from 'moment'
import { get } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiDelete from 'lib/network/apiDelete'
import notify from 'components/atoms/Notify'
import Button from 'components/atoms/Button'
import { useTranslation } from 'react-i18next'
import { IoSearch, IoAdd, IoPencil, IoTrashBin } from 'react-icons/io5'
import useModal from 'hooks/useModal'

import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import PageFooter from 'components/paginationComponent'
import AddEditNewsletterSubscriber from './modals/AddEditNewsletterSubscriber'

const NewsletterSubscriptions = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({})
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState({})
  const [editData, setEditData] = useState(null)
  const [modalKey, setModalKey] = useState(0)

  const fetchList = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    }
    const params = {
      pageNumber: page,
      pageSize: 20,
      search: search || undefined,
      sortBy: sort.sortBy || undefined,
      sortOrder: sort.sortOrder || undefined,
    }
    const response = await apiGet('newsletter-subscriptions', params)
    if (response?.success && response?.data) {
      const list = response.data.subscriptions || []
      setItems(page === 1 ? list : [...items, ...list])
      setPagination(response.data.pagination || {})
    }
    setLoading(false)
  }

  const [SubscriberModal, openSubscriberModal, closeSubscriberModal] = useModal({
    content: <AddEditNewsletterSubscriber key={modalKey} editData={editData} onSuccess={() => fetchList(1)} />,
    title: editData ? t('newsletter.editSubscriber') : t('newsletter.addSubscriber'),
    closeable: true,
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '560px',
      minHeight: 'auto',
      minWidth: '320px',
    },
  })

  useEffect(() => {
    fetchList(1)
  }, [search, sort])

  const handleOpenCreateModal = () => {
    setEditData(null)
    setModalKey(k => k + 1)
    openSubscriberModal()
  }

  const handleOpenEditModal = item => {
    setEditData(item)
    setModalKey(k => k + 1)
    openSubscriberModal()
  }

  const handleDelete = id => {
    notify.deleteConfirm({
      title: t('newsletter.deleteTitle'),
      message: t('newsletter.deleteMessage'),
      onConfirm: async () => {
        const response = await apiDelete(`newsletter-subscriptions/${id}`)
        if (response?.success) {
          notify.success(t('newsletter.deleteSuccess'))
          fetchList(1)
        }
      },
    })
  }

  const columns = useMemo(
    () => [
      {
        name: t('newsletter.columns.email'),
        width: '38%',
        key: 'email',
        sortable: true,
        rendered: item => <span className="text-sm font-normal text-gray-900">{item.email}</span>,
      },
      {
        name: t('newsletter.columns.source'),
        width: '20%',
        key: 'source',
        sortable: true,
        rendered: item => <span className="text-sm text-gray-600">{item.source || t('common.noData')}</span>,
      },
      {
        name: t('newsletter.columns.subscribedAt'),
        width: '28%',
        key: 'createdAt',
        sortable: true,
        rendered: item => {
          const createdTime = get(item, 'createdAt', '')
          const label = createdTime ? moment(createdTime).format('MMM D, YYYY h:mm A') : t('common.noData')
          return <span className="text-sm text-gray-500">{label}</span>
        },
      },
      {
        name: t('newsletter.columns.actions'),
        width: '14%',
        key: 'actions',
        sortable: false,
        rendered: item => (
          <div className="flex space-x-4">
            <IoPencil className="text-dark-600 cursor-pointer" onClick={() => handleOpenEditModal(item)} />
            <IoTrashBin className="text-red-500 cursor-pointer" onClick={() => handleDelete(item._id)} />
          </div>
        ),
      },
    ],
    [t]
  )

  return (
    <Layout
      breadcrumbs={[{ name: t('newsletter.breadcrumb'), pathname: '/newsletter-subscriptions' }]}
      headerRight={
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full justify-end sm:mt-0 sm:ml-4 gap-2 sm:gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <PageFooter currentPage={pagination.pageNumber} totalPages={pagination.totalPages} totalRecords={pagination.totalItems} />
            <div className="mt-1 relative rounded-md shadow-sm flex-1 sm:flex-none">
              <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`} aria-hidden="true">
                <IoSearch className={`${isRTL ? 'ml-3' : 'mr-3'} h-4 w-4 text-gray-400`} aria-hidden="true" />
              </div>
              <input
                onChange={e => setSearch(e.target.value)}
                value={search}
                type="text"
                name="search"
                id="newsletter-search"
                className={`block w-full ${isRTL ? 'pr-9' : 'pl-9'} shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm`}
                placeholder={t('newsletter.searchPlaceholder')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>
          </div>
          <Button onClick={handleOpenCreateModal} className="w-full sm:w-auto flex items-center gap-2">
            <IoAdd className="h-4 w-4" />
            {t('newsletter.addSubscriber')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col">
        {loading && items.length === 0 ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <LoadingIcon />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title={t('newsletter.emptyTitle')} description={t('newsletter.emptyDescription')} />
        ) : (
          <Table
            columns={columns}
            data={items}
            fetchData={() => fetchList((pagination?.pageNumber || 1) + 1)}
            hasMore={pagination?.pageNumber !== pagination?.totalPages}
            sortBy={sort.sortBy}
            sortOrder={sort.sortOrder}
            handleSort={(sortBy, sortOrder) => setSort({ sortBy, sortOrder })}
            loading={loading}
          />
        )}
      </div>
      <SubscriberModal />
    </Layout>
  )
}

export default NewsletterSubscriptions
